import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VoiceAssistantTrigger from './VoiceAssistantTrigger';
import { getNavIcon } from '../constants/navIcons';
import { useVisualViewportFabOffset } from '../hooks/useVisualViewportFabOffset';
import './FloatingActionBar.css';

interface FloatingActionBarProps {
  onDashboard?: () => void;
  onImageUpload?: () => void;
  onJournalEntries?: () => void;
  onContacts?: () => void;
  onMicPress?: () => void;
  isVoiceRecording?: boolean;
  hideVoiceTrigger?: boolean;
}

const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  onDashboard,
  onImageUpload,
  onJournalEntries,
  onContacts,
  onMicPress,
  isVoiceRecording = false,
  hideVoiceTrigger = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useVisualViewportFabOffset(true);

  const goTo = (path: string, handler?: () => void) => {
    if (handler) {
      handler();
      return;
    }
    navigate(path);
  };

  return (
    <footer className="modern-fab-footer" aria-label={t('navigation.voiceAssistant')}>
      <div className="modern-fab-container">
        <div className="modern-fab-center">
          {!hideVoiceTrigger && (
            <VoiceAssistantTrigger
              onClick={onMicPress || (() => {})}
              isRecording={isVoiceRecording}
            />
          )}
        </div>

        <div className="modern-fab-actions">
          <button
            type="button"
            className="modern-fab-action-btn"
            onClick={() => goTo('/dashboard', onDashboard)}
            title={t('navigation.dashboard')}
            aria-label={t('navigation.dashboard')}
          >
            <span className="material-icons">{getNavIcon('dashboard')}</span>
          </button>

          <button
            type="button"
            className="modern-fab-action-btn"
            onClick={() => goTo('/image-processing', onImageUpload)}
            title={t('navigation.imageProcessing')}
            aria-label={t('navigation.imageProcessing')}
          >
            <span className="material-icons">{getNavIcon('imageProcessing')}</span>
          </button>

          <div className="modern-fab-spacer" />

          <button
            type="button"
            className="modern-fab-action-btn"
            onClick={() => goTo('/journal-entries', onJournalEntries)}
            title={t('navigation.journalEntries')}
            aria-label={t('navigation.journalEntries')}
          >
            <span className="material-icons">{getNavIcon('journalEntries')}</span>
          </button>

          <button
            type="button"
            className="modern-fab-action-btn"
            onClick={() => goTo('/contacts', onContacts)}
            title={t('navigation.contacts')}
            aria-label={t('navigation.contacts')}
          >
            <span className="material-icons">{getNavIcon('contacts')}</span>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default FloatingActionBar;
